import { patch } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { type QualificationHolidayFeeOverrides } from "../types";

const holidayFeeOverrideSchema = z.object({
  type: z.enum(["FLAT", "CHARGE_RATE_MULTIPLIER"]),
  increment: z.number(),
});

type HolidayFeeOverridePayload = z.infer<typeof holidayFeeOverrideSchema>;

const updateWorkplaceHolidayFeeOverridesRequestSchema = z.object({
  data: z.object({
    type: z.literal("workplace"),
    id: z.string(),
    attributes: z.object({
      qualificationHolidayFeeOverrides: z.record(z.string(), holidayFeeOverrideSchema.nullable()),
    }),
  }),
});

type UpdateWorkplaceHolidayFeeOverridesRequest = z.infer<
  typeof updateWorkplaceHolidayFeeOverridesRequestSchema
>;

const updateWorkplaceHolidayFeeOverridesResponseSchema = z.object({
  data: z.object({
    type: z.literal("workplace"),
    id: z.string(),
    attributes: z.object({}).passthrough(),
  }),
});

interface UseUpdateWorkplaceHolidayFeeOverridesParams {
  workplaceId: string;
  qualificationHolidayFeeOverrides: QualificationHolidayFeeOverrides;
}

export function useUpdateWorkplaceHolidayFeeOverrides() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workplaceId,
      qualificationHolidayFeeOverrides,
    }: UseUpdateWorkplaceHolidayFeeOverridesParams) => {
      const url = `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}`;

      // Convert undefined values to null for clearing, and ensure proper structure
      const overridesPayload: Record<string, HolidayFeeOverridePayload | null> = {};
      for (const [qualification, override] of Object.entries(qualificationHolidayFeeOverrides)) {
        overridesPayload[qualification] =
          override?.type && typeof override.increment === "number"
            ? { type: override.type, increment: override.increment }
            : null;
      }

      const requestData: UpdateWorkplaceHolidayFeeOverridesRequest = {
        data: {
          type: "workplace",
          id: workplaceId,
          attributes: {
            qualificationHolidayFeeOverrides: overridesPayload,
          },
        },
      };

      const response = await patch({
        url,
        requestSchema: updateWorkplaceHolidayFeeOverridesRequestSchema,
        responseSchema: updateWorkplaceHolidayFeeOverridesResponseSchema,
        data: requestData,
      });

      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate the facility holiday fee overrides query to trigger refetch
      void queryClient.invalidateQueries({
        queryKey: [
          `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile/details`,
          { facilityIds: variables.workplaceId, projection: "qualificationHolidayFeeOverrides" },
        ],
      });
    },
  });
}
